import { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../lib/auth";

type Merchant = {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  created_at: string;
};

function formatDate(s?: string | null) {
  if (!s) return "-";
  try {
    const d = new Date(s);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return s;
  }
}

export default function Merchants() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [openForm, setOpenForm] = useState<null | { mode: "create" | "edit"; row?: Merchant }>(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });

  const [openPw, setOpenPw] = useState<null | Merchant>(null);
  const [newPw, setNewPw] = useState("");

  const canSubmit = useMemo(() => form.name.trim().length > 0, [form.name]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams();
      if (q.trim()) qs.set("q", q.trim());
      const r = await authFetch(`/api/admin/merchants?${qs.toString()}`);
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.message || "failed");
      setRows(data.rows || []);
    } catch (e: any) {
      setErr(e?.message || "로드 실패");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setForm({ name: "", phone: "", email: "", password: "" });
    setOpenForm({ mode: "create" });
  }

  function openEdit(row: Merchant) {
    setForm({ name: row.name || "", phone: row.phone || "", email: row.email || "", password: "" });
    setOpenForm({ mode: "edit", row });
  }

  async function submitForm() {
    if (!canSubmit) return;

    try {
      const mode = openForm?.mode;
      if (!mode) return;

      if (mode === "create") {
        if (!form.password.trim()) {
          alert("등록 시 비밀번호는 필수입니다.");
          return;
        }
        const r = await authFetch(`/api/admin/merchants`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name.trim(),
            phone: form.phone.trim() || null,
            email: form.email.trim() || null,
            password: form.password.trim(),
          }),
        });
        const data = await r.json();
        if (!data?.ok) throw new Error(data?.message || "등록 실패");
      } else {
        const id = openForm.row!.id;
        const r = await authFetch(`/api/admin/merchants/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name.trim(),
            phone: form.phone.trim() || null,
            email: form.email.trim() || null,
          }),
        });
        const data = await r.json();
        if (!data?.ok) throw new Error(data?.message || "수정 실패");
      }

      setOpenForm(null);
      await load();
    } catch (e: any) {
      alert(e?.message || "실패");
    }
  }

  async function submitPw() {
    if (!openPw) return;
    if (!newPw.trim()) return alert("새 비밀번호를 입력해 주세요.");

    try {
      const r = await authFetch(`/api/admin/merchants/${openPw.id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPw.trim() }),
      });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.message || "PW 변경 실패");
      setOpenPw(null);
      setNewPw("");
      alert("비밀번호가 변경되었습니다.");
    } catch (e: any) {
      alert(e?.message || "실패");
    }
  }

  async function remove(row: Merchant) {
    if (!confirm(`삭제하시겠습니까?\n\n${row.name} (id=${row.id})`)) return;
    try {
      const r = await authFetch(`/api/admin/merchants/${row.id}`, { method: "DELETE" });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.message || "삭제 실패");
      await load();
    } catch (e: any) {
      alert(e?.message || "실패");
    }
  }

  return (
    <div className="jp-admin-merchants space-y-4">
      {/* 헤더: 모바일 스택 */}
      <div className="jp-admin-page-heading">
        <div>
          <h1>가맹점 관리</h1>
          <p className="mt-1 text-sm text-slate-500">가맹점 계정을 관리합니다.</p>
        </div>

        <button
          onClick={openCreate}
          className="h-10 w-full rounded-lg bg-slate-900 px-3 text-sm font-medium text-white hover:bg-slate-800 sm:w-auto"
        >
          등록
        </button>
      </div>

      {/* 검색: 모바일 2줄 대응 */}
      <div className="jp-admin-filter-bar jp-admin-merchants-toolbar flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="가맹점 검색"
          placeholder="이름·연락처·이메일 검색"
          className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
        />
        <button
          onClick={load}
          className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:w-auto"
        >
          검색
        </button>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* ✅ 모바일 카드 */}
      <div className="jp-admin-responsive-cards">
        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            불러오는 중...
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            데이터가 없습니다.
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{r.name}</div>
                  <div className="mt-1 text-xs text-slate-500">#{r.id}</div>
                </div>
                <div className="text-xs text-slate-500">{formatDate(r.created_at)}</div>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">연락처</span>
                  <span className="text-right">{r.phone || "-"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">이메일</span>
                  <span className="text-right break-all">{r.email || "-"}</span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <button
                  onClick={() => openEdit(r)}
                  className="h-10 rounded-lg bg-slate-800 text-xs font-semibold text-white hover:bg-slate-700"
                >
                  수정
                </button>
                <button
                  onClick={() => setOpenPw(r)}
                  className="h-10 rounded-lg bg-slate-800 text-xs font-semibold text-white hover:bg-slate-700"
                >
                  PW수정
                </button>
                <button
                  onClick={() => remove(r)}
                  className="h-10 rounded-lg bg-rose-600 text-xs font-semibold text-white hover:bg-rose-500"
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ✅ 데스크탑 테이블 */}
      <div className="jp-admin-responsive-table rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-3 text-left">번호</th>
                <th className="px-3 py-3 text-left">이름</th>
                <th className="px-3 py-3 text-left">연락처</th>
                <th className="px-3 py-3 text-left">이메일</th>
                <th className="px-3 py-3 text-left">등록일자</th>
                <th className="px-3 py-3 text-left">관리</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>
                    불러오는 중...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>
                    데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-3">{r.id}</td>
                    <td className="px-3 py-3 font-medium text-slate-900">{r.name}</td>
                    <td className="px-3 py-3">{r.phone || "-"}</td>
                    <td className="px-3 py-3 break-all">{r.email || "-"}</td>
                    <td className="px-3 py-3">{formatDate(r.created_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => setOpenPw(r)}
                          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                        >
                          PW수정
                        </button>
                        <button
                          onClick={() => remove(r)}
                          className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 등록/수정 모달 */}
      {openForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl max-h-[90vh]">
            <div className="border-b px-5 py-4">
              <div className="text-base font-semibold text-slate-900">
                {openForm.mode === "create" ? "가맹점 등록" : "가맹점 수정"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {openForm.mode === "create"
                  ? "신규 가맹점에는 관리자 권한을 자동 부여하지 않습니다."
                  : "비밀번호는 PW수정에서 변경합니다."}
              </div>
            </div>

            <div className="space-y-3 overflow-y-auto px-5 py-4">
              <Field label="이름" value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} />
              <Field label="연락처" value={form.phone} onChange={(v) => setForm((p) => ({ ...p, phone: v }))} />
              <Field label="이메일" value={form.email} onChange={(v) => setForm((p) => ({ ...p, email: v }))} />
              {openForm.mode === "create" && (
                <Field
                  label="비밀번호 (필수)"
                  type="password"
                  value={form.password}
                  onChange={(v) => setForm((p) => ({ ...p, password: v }))}
                />
              )}
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                onClick={() => setOpenForm(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={submitForm}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PW 수정 모달 */}
      {openPw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-xl max-h-[90vh]">
            <div className="border-b px-5 py-4">
              <div className="text-base font-semibold text-slate-900">PW 수정</div>
              <div className="mt-1 text-xs text-slate-500">
                대상: {openPw.name} (id={openPw.id})
              </div>
            </div>

            <div className="space-y-3 overflow-y-auto px-5 py-4">
              <Field label="새 비밀번호" type="password" value={newPw} onChange={setNewPw} />
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                onClick={() => {
                  setOpenPw(null);
                  setNewPw("");
                }}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={submitPw}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                변경
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-slate-600">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
      />
    </label>
  );
}
